<template>
  <el-dialog title="流水详情" :visible.sync="dialogVisible" width="80%" @close="handleClose" :append-to-body="true">
    <div v-if="currentRow && currentRow.full_info">
      <!-- 解析full_info JSON数据 -->
      <div v-if="bankInfo">
        <!-- 基础信息 -->
        <el-card class="detail-section" shadow="never">
          <div slot="header" class="section-header">基础信息</div>
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item label="交易流水号">{{ bankInfo.transactionSerialNumber }}</el-descriptions-item>
            <el-descriptions-item label="银行流水号">{{ bankInfo.bankSerialNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="唯一键">{{ bankInfo.uniqueKey }}</el-descriptions-item>
            <el-descriptions-item label="原始流水号">{{ bankInfo.originalSerialNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="结算流水号">{{ bankInfo.settlementSerialNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="直连流水号">{{ bankInfo.directSerialNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="交易时间">{{ formatTimestamp(bankInfo.bankTransactionDate) }}</el-descriptions-item>
            <el-descriptions-item label="起息日">{{ bankInfo.valueDate ? formatTimestamp(bankInfo.valueDate) : '-' }}</el-descriptions-item>
            <el-descriptions-item label="记账日期">{{ bankInfo.bookingDate ? formatTimestamp(bankInfo.bookingDate) : '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 账户信息 -->
        <el-card class="detail-section" shadow="never">
          <div slot="header" class="section-header">账户信息</div>
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item label="银行账号">{{ bankInfo.accountNo }}</el-descriptions-item>
            <el-descriptions-item label="账户名称">{{ bankInfo.accountName }}</el-descriptions-item>
            <el-descriptions-item label="开户行">{{ bankInfo.openBank || '-' }}</el-descriptions-item>
            <el-descriptions-item label="银行类型">{{ bankInfo.bankType }}</el-descriptions-item>
            <el-descriptions-item label="账户性质">{{ bankInfo.accountNature || '-' }}</el-descriptions-item>
            <el-descriptions-item label="账户状态">
              {{ bankInfo.accountStatus === '0' ? '正常' : '注销' }}
            </el-descriptions-item>
            <el-descriptions-item label="币种">
              {{ bankInfo.currency === '10' ? '人民币' : bankInfo.currency }}
            </el-descriptions-item>
            <el-descriptions-item label="虚拟户账号">{{ bankInfo.virtualAccount || '-' }}</el-descriptions-item>
            <el-descriptions-item label="虚拟户名称">{{ bankInfo.virtualAccountName || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 金额信息 -->
        <el-card class="detail-section" shadow="never">
          <div slot="header" class="section-header">金额信息</div>
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item label="发生额">
              <span :class="bankInfo.loanType === '1' ? 'text-danger' : 'text-success'" style="font-weight: bold; font-size: 16px;">
                {{ bankInfo.incurredAmount }}
              </span>
            </el-descriptions-item>
            <el-descriptions-item label="借贷类型">
              <el-tag :type="bankInfo.loanType === '1' ? 'danger' : 'success'" size="medium">
                {{ bankInfo.loanType === '1' ? '借' : '贷' }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="交易后余额">{{ bankInfo.accountBalance || '-' }}</el-descriptions-item>
            <el-descriptions-item label="交易服务费">{{ bankInfo.transactionFee || '-' }}</el-descriptions-item>
            <el-descriptions-item label="服务费率">{{ bankInfo.transactionFeeRate || '-' }}</el-descriptions-item>
            <el-descriptions-item label="交易金额">{{ bankInfo.transactionAmount || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 对方信息 -->
        <el-card class="detail-section" shadow="never">
          <div slot="header" class="section-header">对方信息</div>
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item label="对方账号">{{ bankInfo.oppositeAccount || '-' }}</el-descriptions-item>
            <el-descriptions-item label="对方户名">{{ bankInfo.oppositeName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="对方开户行">{{ bankInfo.oppositeOpeningBank || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 业务信息 -->
        <el-card class="detail-section" shadow="never">
          <div slot="header" class="section-header">业务信息</div>
          <el-descriptions :column="2" border size="small">
            <el-descriptions-item label="摘要">{{ bankInfo.digest || '-' }}</el-descriptions-item>
            <el-descriptions-item label="用途">{{ bankInfo.purpose || '-' }}</el-descriptions-item>
            <el-descriptions-item label="备注">{{ bankInfo.remark || '-' }}</el-descriptions-item>
            <el-descriptions-item label="附言">{{ bankInfo.postscript || '-' }}</el-descriptions-item>
            <el-descriptions-item label="交易代码">{{ bankInfo.transactionCode || '-' }}</el-descriptions-item>
            <el-descriptions-item label="交易代码名称">{{ bankInfo.transactionCodeName || '-' }}</el-descriptions-item>
            <el-descriptions-item label="款项性质代码">{{ bankInfo.paymentNature || '-' }}</el-descriptions-item>
            <el-descriptions-item label="款项性质标记">
              {{ getPaymentNatureFlagText(bankInfo.paymentNatureFlag) }}
            </el-descriptions-item>
            <el-descriptions-item label="明细类型">
              {{ bankInfo.detailType === '1' ? '当日明细' : '历史明细' }}
            </el-descriptions-item>
            <el-descriptions-item label="明细来源">
              {{ bankInfo.detailSource === 'B' ? '银行直联' : (bankInfo.detailSource === 'U' ? '手工导入' : '-') }}
            </el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- 单位与对账信息 -->
        <el-card class="detail-section" shadow="never">
          <div slot="header" class="section-header">单位与对账信息</div>
          <el-descriptions :column="3" border size="small">
            <el-descriptions-item label="单位编码">{{ bankInfo.unitCode }}</el-descriptions-item>
            <el-descriptions-item label="单位名称">{{ bankInfo.unitName }}</el-descriptions-item>
            <el-descriptions-item label="显示单位编码">{{ bankInfo.displayUnitCode || '-' }}</el-descriptions-item>
            <el-descriptions-item label="对账码">{{ bankInfo.checkCode || '-' }}</el-descriptions-item>
            <el-descriptions-item label="对账状态">
              {{ getCheckTypeText(bankInfo.checkType) }}
            </el-descriptions-item>
            <el-descriptions-item label="关联客户号">{{ bankInfo.associatedCustomerNumber || '-' }}</el-descriptions-item>
            <el-descriptions-item label="科目代码">{{ bankInfo.subjectCode || '-' }}</el-descriptions-item>
            <el-descriptions-item label="凭证编号">{{ bankInfo.voucherCode || '-' }}</el-descriptions-item>
            <el-descriptions-item label="业务参考号">{{ bankInfo.businessReferenceCode || '-' }}</el-descriptions-item>
          </el-descriptions>
        </el-card>

        <!-- ERP与扩展信息(可折叠) -->
        <el-collapse v-model="activeCollapse">
          <el-collapse-item name="erp">
            <template slot="title">
              <span style="font-weight: bold;">ERP与扩展信息 (点击展开)</span>
            </template>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="ERP业务参考号">{{ bankInfo.erpSerialNumber || '-' }}</el-descriptions-item>
              <el-descriptions-item label="客商编号">{{ bankInfo.merchantNumber || '-' }}</el-descriptions-item>
              <el-descriptions-item label="客商名称">{{ bankInfo.merchantName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="交易识别码">{{ bankInfo.corporateIdentityCode || '-' }}</el-descriptions-item>
              <el-descriptions-item label="母公司账号">{{ bankInfo.parentSubCompanyAccount || '-' }}</el-descriptions-item>
              <el-descriptions-item label="母公司名称">{{ bankInfo.parentSubCompanyName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="交易类型">{{ bankInfo.protocolTypeName || '-' }}</el-descriptions-item>
              <el-descriptions-item label="退票原因">{{ bankInfo.refundReason || '-' }}</el-descriptions-item>
              <el-descriptions-item label="批次序列号">{{ bankInfo.serialNo || '-' }}</el-descriptions-item>
              <el-descriptions-item label="更新时间">{{ bankInfo.updateTime ? formatTimestamp(bankInfo.updateTime) : '-' }}</el-descriptions-item>
            </el-descriptions>

            <!-- 支付申请备注 -->
            <div v-if="hasPayApplyRemarks" style="margin-top: 15px;">
              <div style="font-weight: bold; margin-bottom: 10px;">支付申请备注:</div>
              <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="备注1">{{ bankInfo.payApplyRemark1 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="备注2">{{ bankInfo.payApplyRemark2 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="备注3">{{ bankInfo.payApplyRemark3 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="备注4">{{ bankInfo.payApplyRemark4 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="备注5">{{ bankInfo.payApplyRemark5 || '-' }}</el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 个性化字段 -->
            <div v-if="hasPersonalizeFields" style="margin-top: 15px;">
              <div style="font-weight: bold; margin-bottom: 10px;">银行个性化字段:</div>
              <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="个性化字段1">
                  <div v-html="decodeHtml(bankInfo.personalizeField1)"></div>
                </el-descriptions-item>
                <el-descriptions-item label="个性化字段2">{{ bankInfo.personalizeField2 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="个性化字段3">{{ bankInfo.personalizeField3 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="个性化字段4">{{ bankInfo.personalizeField4 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="个性化字段5">{{ bankInfo.personalizeField5 || '-' }}</el-descriptions-item>
              </el-descriptions>
            </div>

            <!-- 预留字段 -->
            <div v-if="hasReserveFields" style="margin-top: 15px;">
              <div style="font-weight: bold; margin-bottom: 10px;">预留字段:</div>
              <el-descriptions :column="2" border size="small">
                <el-descriptions-item label="预留字段1">{{ bankInfo.reserveField1 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="预留字段2">{{ bankInfo.reserveField2 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="预留字段3">{{ bankInfo.reserveField3 || '-' }}</el-descriptions-item>
                <el-descriptions-item label="预留字段4">{{ bankInfo.reserveField4 || '-' }}</el-descriptions-item>
              </el-descriptions>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'CbsFlowDetail',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    flowData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      dialogVisible: false,
      currentRow: null,
      activeCollapse: []
    };
  },
  computed: {
    bankInfo() {
      if (!this.currentRow || !this.currentRow.full_info) {
        return null;
      }
      try {
        return typeof this.currentRow.full_info === 'string'
          ? JSON.parse(this.currentRow.full_info)
          : this.currentRow.full_info;
      } catch (e) {
        console.error('解析full_info失败:', e);
        return null;
      }
    },
    hasPayApplyRemarks() {
      if (!this.bankInfo) return false;
      return this.bankInfo.payApplyRemark1 || this.bankInfo.payApplyRemark2 ||
             this.bankInfo.payApplyRemark3 || this.bankInfo.payApplyRemark4 ||
             this.bankInfo.payApplyRemark5;
    },
    hasPersonalizeFields() {
      if (!this.bankInfo) return false;
      return this.bankInfo.personalizeField1 || this.bankInfo.personalizeField2 ||
             this.bankInfo.personalizeField3 || this.bankInfo.personalizeField4 ||
             this.bankInfo.personalizeField5;
    },
    hasReserveFields() {
      if (!this.bankInfo) return false;
      return this.bankInfo.reserveField1 || this.bankInfo.reserveField2 ||
             this.bankInfo.reserveField3 || this.bankInfo.reserveField4;
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val;
    },
    dialogVisible(val) {
      this.$emit('update:visible', val);
    },
    flowData: {
      handler(val) {
        if (val) {
          this.currentRow = val;
          this.activeCollapse = [];
        }
      },
      immediate: true
    }
  },
  methods: {
    handleClose() {
      this.$emit('close');
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
    getCheckTypeText(checkType) {
      const map = {
        '0': '未对账',
        '1': '自动对账',
        '2': '手工对账',
        '3': '自动对账(退票)',
        '4': '手工对账(退票)',
        '5': '对账中(退票)',
        '6': '自动对账(外汇)',
        '7': '手工对账(外汇)'
      };
      return map[checkType] || checkType || '-';
    },
    getPaymentNatureFlagText(flag) {
      const map = {
        '1': '未匹配',
        '2': '手工处理',
        '3': '系统处理',
        '4': '匹配失败'
      };
      return map[flag] || flag || '-';
    },
    decodeHtml(html) {
      if (!html) return '-';
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    }
  }
};
</script>

<style scoped>
.detail-section {
  margin-bottom: 15px;
}

.section-header {
  font-weight: bold;
  font-size: 14px;
  color: #303133;
}

.text-success {
  color: #67c23a;
}

.text-danger {
  color: #f56c6c;
}
</style>



// WEBPACK FOOTER //
// src/components/Admin/Order/order/CbsFlowDetail.vue